import {
  buildDateWithOptionalTime,
  extractTimeFromText,
  formatLocalTimeForStorage,
  parseDateForStorage,
} from './importLogsParsingTime';
import { normalizeTextForImport } from './importLogsParsingText';
import {
  getImportDateHeading,
  getInlineImportDateHeading,
  getLeadingImportDateHeading,
  getRepeatedInlineImportDateMatches,
} from './importLogsDateAnchors';
import {
  CARE_DATE_MONTH_NAME_PATTERN,
  createCareTimelineHeadingRegex,
} from '../../../shared/careDateTimePatterns';

export {
  getImportDateHeading,
  getLeadingImportDateHeading,
};

const MONTH_NAME_PATTERN = CARE_DATE_MONTH_NAME_PATTERN;
const STRONG_IMPORT_EMBEDDED_DATE_PATTERN = createCareTimelineHeadingRegex({
  requireTime: true,
  requireWeekday: true,
});
const IMPORT_RANGE_TREND_PATTERN = /\b(?:streak|trend|zero episodes|summary)\b/i;
const IMPORT_RANGE_CONNECTOR_PATTERN = /\b(?:through|to|until)\b/i;
const IMPORT_NUMERIC_DATE_PATTERN = /\b(?:\d{1,2}\/\d{1,2}(?:\/\d{2,4})?|\d{4}-\d{2}-\d{2})\b/i;
const IMPORT_RELATIVE_DATE_PATTERN = /\b(?:yesterday|today|tomorrow|last\s+(?:mon|tues|wednes|thurs|fri|satur|sun)day|next\s+(?:mon|tues|wednes|thurs|fri|satur|sun)day|next week|last week|spring break)\b/i;
const IMPORT_RANGE_DATE_PATTERN = new RegExp(
  `\\b(?:from\\s+${MONTH_NAME_PATTERN}\\s+\\d{1,2}(?:st|nd|rd|th)?\\s+(?:to|through|until)\\s+${MONTH_NAME_PATTERN}\\s+\\d{1,2}(?:st|nd|rd|th)?|between\\s+${MONTH_NAME_PATTERN}\\s+\\d{1,2}(?:st|nd|rd|th)?\\s+and\\s+${MONTH_NAME_PATTERN}\\s+\\d{1,2}(?:st|nd|rd|th)?|${MONTH_NAME_PATTERN}\\s+\\d{1,2}(?:st|nd|rd|th)?\\s+(?:to|through|until)\\s+${MONTH_NAME_PATTERN}\\s+\\d{1,2}(?:st|nd|rd|th)?)\\b`,
  'i'
);

const isProtectedImportRangeSummary = (text = '') => {
  const source = String(text || '').trim();
  if (!source) {
    return false;
  }

  return IMPORT_RANGE_TREND_PATTERN.test(source) && IMPORT_RANGE_CONNECTOR_PATTERN.test(source);
};

const looksLikeUnresolvedImportDateText = (text = '') => {
  const source = String(text || '').trim();
  if (!source) {
    return false;
  }

  return new RegExp(`${MONTH_NAME_PATTERN}`, 'i').test(source)
    || IMPORT_NUMERIC_DATE_PATTERN.test(source)
    || IMPORT_RELATIVE_DATE_PATTERN.test(source)
    || IMPORT_RANGE_DATE_PATTERN.test(source);
};

const hasStrongUnresolvedImportDateText = (text = '') => STRONG_IMPORT_EMBEDDED_DATE_PATTERN.test(String(text || '').trim());

const getImportRowDateInspectionText = (row = {}) => ([
  row?.title,
  row?.body,
  row?.note,
  row?.text,
  row?.content,
  row?.sourceText,
  row?.originalText,
].map((value) => String(value || '').trim()).filter(Boolean).join(' \n '));

const normalizePromotedImportText = (value = '') => {
  const normalized = String(value || '')
    .replace(/^[\s,:;\-–—]+/u, '')
    .replace(/\s+/g, ' ')
    .trim();

  if (!normalized) {
    return '';
  }

  return normalized.replace(/^[a-z]/u, (match) => match.toUpperCase());
};

const stripLeadingImportDateHeading = (text = '', context = null) => {
  const rawText = String(text || '').trim();
  const headingText = String(context?.headingText || context?.dateLabel || '').trim();
  if (!rawText || !headingText) {
    return rawText;
  }

  if (!rawText.toLowerCase().startsWith(headingText.toLowerCase())) {
    return rawText;
  }

  return rawText.slice(headingText.length).replace(/^[\s,:-]+/, '').trim();
};

const stripLeadingPromotedImportDateText = (text = '', heading = null) => {
  const rawText = String(text || '').trim();
  const headingText = String(heading?.headingText || heading?.dateLabel || '').trim();
  if (!rawText || !headingText) {
    return rawText;
  }

  const directMatch = rawText.toLowerCase().startsWith(headingText.toLowerCase());
  if (directMatch) {
    return normalizePromotedImportText(rawText.slice(headingText.length));
  }

  const fallbackHeading = getLeadingImportDateHeading(rawText, {
    defaultYear: heading?.date instanceof Date && !Number.isNaN(heading.date.getTime())
      ? heading.date.getFullYear()
      : null,
  });
  if (fallbackHeading?.bodyText) {
    return normalizePromotedImportText(fallbackHeading.bodyText);
  }

  return rawText;
};

const getImportRowText = (row = {}) => String(
  row?.body
  || row?.note
  || row?.text
  || row?.content
  || row?.sourceText
  || row?.originalText
  || row?.title
  || ''
).trim();

const replaceImportRowDisplayText = (row = {}, nextText = '', {
  originalRowText = '',
} = {}) => {
  const cleanedText = normalizePromotedImportText(nextText);
  if (!cleanedText) {
    return row;
  }

  const originalText = String(originalRowText || getImportRowText(row) || '').trim();
  const cleanTitle = (value = '') => {
    const candidate = String(value || '').trim();
    if (!candidate) {
      return candidate;
    }

    const stripped = stripLeadingPromotedImportDateText(candidate, {
      headingText: originalText,
      dateLabel: originalText,
    });
    if (stripped && stripped !== candidate) {
      return stripped;
    }

    return candidate === originalText ? cleanedText : candidate;
  };

  return {
    ...row,
    note: cleanedText || row?.note || '',
    body: cleanedText || row?.body || '',
    text: cleanedText || row?.text || '',
    content: cleanedText || row?.content || '',
    details: cleanedText || row?.details || '',
    description: cleanedText || row?.description || '',
    summary: cleanedText || row?.summary || '',
    value: cleanedText || row?.value || '',
    sourceText: cleanedText || row?.sourceText || '',
    originalText: cleanedText || row?.originalText || '',
    title: cleanTitle(row?.title),
    titlePrefix: cleanTitle(row?.titlePrefix),
  };
};

const isSameImportCalendarDay = (left, right) => {
  const leftDate = left instanceof Date ? left : parseDateForStorage(left);
  const rightDate = right instanceof Date ? right : parseDateForStorage(right);
  if (!(leftDate instanceof Date) || Number.isNaN(leftDate.getTime())) {
    return false;
  }
  if (!(rightDate instanceof Date) || Number.isNaN(rightDate.getTime())) {
    return false;
  }

  return leftDate.getFullYear() === rightDate.getFullYear()
    && leftDate.getMonth() === rightDate.getMonth()
    && leftDate.getDate() === rightDate.getDate();
};

const withImportDateWarningMetadata = (row = {}, fallbackDate = new Date()) => {
  const rowText = getImportRowDateInspectionText(row);
  const rowMetadata = row?.metadata || {};
  const rowDate = parseDateForStorage(row?.date);
  const usesFallbackDate = isSameImportCalendarDay(rowDate, fallbackDate);
  const alreadyResolved = Boolean(
    rowMetadata.dateSource === 'embedded_date_time'
    || rowMetadata.dateSource === 'inline_promoted'
    || (rowMetadata.datePrecision === 'day' && rowMetadata.dateLabel)
  );
  const suspiciousDateText = looksLikeUnresolvedImportDateText(rowText);
  const strongSuspiciousDateText = hasStrongUnresolvedImportDateText(rowText);

  if (!usesFallbackDate || alreadyResolved || !suspiciousDateText) {
    if (!rowMetadata.dateWarning && !rowMetadata.dateWarningReason) {
      return row;
    }

    return {
      ...row,
      metadata: {
        ...rowMetadata,
        dateWarning: false,
        dateWarningLevel: '',
        dateWarningReason: '',
      },
    };
  }

  return {
    ...row,
    metadata: {
      ...rowMetadata,
      dateWarning: true,
      dateWarningLevel: strongSuspiciousDateText ? 'high' : '',
      dateWarningReason: strongSuspiciousDateText
        ? 'strong_embedded_date_not_applied'
        : 'possible_embedded_date_not_applied',
    },
  };
};

export const splitImportTextByEmbeddedDateAnchors = (text = '') => {
  const sourceText = String(text || '').trim();
  const matches = isProtectedImportRangeSummary(sourceText)
    ? []
    : getRepeatedInlineImportDateMatches(sourceText);
  if (matches.length <= 1) {
    return [sourceText].filter(Boolean);
  }

  const segments = matches.map((match, index) => {
    const nextIndex = index + 1 < matches.length ? matches[index + 1].index : sourceText.length;
    return sourceText.slice(match.index, nextIndex).trim();
  }).filter(Boolean);

  return segments.length > 1 ? segments : [sourceText].filter(Boolean);
};

const splitImportedRowByEmbeddedDateAnchors = (row = {}) => {
  const rowText = getImportRowText(row);
  const segments = splitImportTextByEmbeddedDateAnchors(rowText);

  if (segments.length <= 1) {
    return [row];
  }

  return segments.map((segment, index) => {
    const cleanedSegment = normalizePromotedImportText(segment);
    return {
      ...row,
      id: row?.id ? `${row.id}::split-${index + 1}` : row?.id,
      note: cleanedSegment || row?.note || '',
      body: cleanedSegment || row?.body || '',
      text: cleanedSegment || row?.text || '',
      content: cleanedSegment || row?.content || '',
      details: cleanedSegment || row?.details || '',
      description: cleanedSegment || row?.description || '',
      summary: cleanedSegment || row?.summary || '',
      value: cleanedSegment || row?.value || '',
      sourceText: cleanedSegment || row?.sourceText || '',
      originalText: cleanedSegment || row?.originalText || '',
      title: cleanedSegment,
      titlePrefix: row?.titlePrefix || row?.category || '',
      metadata: {
        ...(row?.metadata || {}),
        splitFromEmbeddedDateAnchors: true,
        originalSegmentSourceText: rowText,
      },
    };
  });
};

const expandImportedRowsByEmbeddedDateAnchors = (rows = []) => (
  Array.isArray(rows)
    ? rows.flatMap((row) => splitImportedRowByEmbeddedDateAnchors(row))
    : []
);

export const extractImportDateContexts = (rawText = '', {
  fallbackDate = new Date(),
} = {}) => {
  const lines = String(rawText || '')
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map((line) => String(line || '').trim());

  const contexts = [];
  let current = null;
  const fallbackYearDate = parseDateForStorage(fallbackDate);
  const resolvedFallbackDate = fallbackYearDate instanceof Date && !Number.isNaN(fallbackYearDate.getTime())
    ? fallbackYearDate
    : new Date();
  let currentYear = fallbackYearDate instanceof Date && !Number.isNaN(fallbackYearDate.getTime())
    ? fallbackYearDate.getFullYear()
    : new Date().getFullYear();

  const flush = () => {
    if (!current) {
      return;
    }

    const bodyText = current.lines.join(' ').replace(/\s+/g, ' ').trim();
    if (current.date || bodyText) {
      contexts.push({
        date: current.date || null,
        dateLabel: current.dateLabel || '',
        datePrecision: current.datePrecision || '',
        headingText: current.headingText || '',
        timeValue: current.timeValue || null,
        timeLabel: current.timeLabel || '',
        yearResolutionStrategy: current.yearResolutionStrategy || '',
        yearAdjustedFromFuture: Boolean(current.yearAdjustedFromFuture),
        bodyText,
      });
    }
    current = null;
  };

  lines.forEach((line) => {
    if (!line) {
      if (current) {
        current.lines.push('');
      }
      return;
    }

    const heading = getLeadingImportDateHeading(line, {
      defaultYear: currentYear,
      fallbackDate: new Date(currentYear, resolvedFallbackDate.getMonth(), resolvedFallbackDate.getDate()),
      sourceText: rawText,
    });
    if (heading) {
      flush();
      const headingHasExplicitYear = !heading.yearResolutionStrategy;
      currentYear = (
        headingHasExplicitYear
        && heading.date instanceof Date
        && !Number.isNaN(heading.date.getTime())
      )
        ? heading.date.getFullYear()
        : currentYear;
      current = {
        ...heading,
        lines: [],
      };
      if (heading.bodyText) {
        current.lines.push(heading.bodyText);
      }
      return;
    }

    if (!current) {
      current = {
        date: null,
        dateLabel: '',
        datePrecision: '',
        headingText: '',
        lines: [],
      };
    }

    current.lines.push(line);
  });

  flush();
  return contexts;
};

const hasMeaningfulTime = (value) => {
  const date = value instanceof Date ? value : parseDateForStorage(value);
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return false;
  }

  return date.getHours() !== 0
    || date.getMinutes() !== 0
    || date.getSeconds() !== 0
    || date.getMilliseconds() !== 0;
};

export const applyImportDateContextToImportedRows = (rows = [], rawText = '', {
  fallbackDate = new Date(),
} = {}) => {
  const expandedRows = expandImportedRowsByEmbeddedDateAnchors(rows);
  const contexts = extractImportDateContexts(rawText, { fallbackDate }).filter((context) => context?.date);
  if (!Array.isArray(expandedRows) || expandedRows.length === 0 || !contexts.length) {
    return Array.isArray(expandedRows)
      ? expandedRows.map((row) => {
          const rowText = getImportRowText(row);
          const rowMetadata = row?.metadata || {};
          const parsedFallbackDate = parseDateForStorage(fallbackDate) || new Date();
          const inlineHeading = getInlineImportDateHeading(rowText, {
            defaultYear: parsedFallbackDate.getFullYear(),
            fallbackDate: parsedFallbackDate,
            sourceText: rawText || rowText,
          });
          if (!inlineHeading?.date) {
            return withImportDateWarningMetadata(row, fallbackDate);
          }

          const cleanedRowText = stripLeadingPromotedImportDateText(rowText, inlineHeading);
          const timeInspectionText = cleanedRowText || rowText;
          const extractedRowTime = inlineHeading.timeValue || extractTimeFromText(timeInspectionText).time || null;
          const inlineResolvedDate = extractedRowTime
            ? buildDateWithOptionalTime(inlineHeading.date, extractedRowTime, timeInspectionText)
            : new Date(inlineHeading.date);
          const displayRow = cleanedRowText
            ? replaceImportRowDisplayText(row, cleanedRowText, { originalRowText: rowText })
            : row;

          return withImportDateWarningMetadata({
            ...displayRow,
            date: inlineResolvedDate,
            localTime: displayRow?.localTime || extractedRowTime || null,
            timeSource: row?.timeSource && row.timeSource !== 'missing'
              ? row.timeSource
              : (extractedRowTime ? 'explicit' : row?.timeSource),
            metadata: {
              ...rowMetadata,
              datePrecision: inlineHeading.datePrecision,
              dateLabel: inlineHeading.dateLabel,
              dateHeading: inlineHeading.headingText || inlineHeading.dateLabel,
              dateContext: {
                label: inlineHeading.dateLabel,
                precision: inlineHeading.datePrecision,
                headingText: inlineHeading.headingText || inlineHeading.dateLabel,
                source: 'embedded_date_time',
                yearResolutionStrategy: inlineHeading.yearResolutionStrategy || '',
              },
              dateSource: 'embedded_date_time',
              yearResolutionStrategy: inlineHeading.yearResolutionStrategy || '',
              yearAdjustedFromFuture: Boolean(inlineHeading.yearAdjustedFromFuture),
            },
          }, fallbackDate);
        })
      : [];
  }

  const parsedFallbackDate = parseDateForStorage(fallbackDate) || new Date();
  const fallbackDateKey = parsedFallbackDate.toDateString();
  let activeContextIndex = -1;

  return expandedRows.map((row) => {
    const rowText = getImportRowText(row);
    const normalizedRowText = normalizeTextForImport(rowText);

    if (normalizedRowText) {
      const searchStart = Math.max(activeContextIndex, 0);
      for (let contextIndex = searchStart; contextIndex < contexts.length; contextIndex += 1) {
        const context = contexts[contextIndex];
        const contextText = normalizeTextForImport(context.bodyText || context.headingText || '');
        if (!contextText) {
          continue;
        }

        const rowSnippet = normalizedRowText.slice(0, 48);
        const contextSnippet = contextText.slice(0, 48);

        if (contextText.includes(rowSnippet) || normalizedRowText.includes(contextSnippet)) {
          activeContextIndex = contextIndex;
          break;
        }
      }
    }

    const rowDate = parseDateForStorage(row?.date);
    const rowDateKey = rowDate.toDateString();
    const rowMetadata = row?.metadata || {};
    const rowHasSpecificDate = Boolean(
      rowMetadata.datePrecision === 'day'
      || rowMetadata.dateLabel
      || rowMetadata.dateHeading
      || rowMetadata.dateContext
    );

    const inlineHeading = (
      !rowHasSpecificDate
      || rowDateKey === fallbackDateKey
      || (
        rowMetadata.datePrecision === 'month'
        && normalizeTextForImport(rowText).includes(normalizeTextForImport(String(rowMetadata.dateLabel || '')))
      )
    )
      ? getInlineImportDateHeading(rowText, {
          defaultYear: (
            activeContextIndex >= 0
            && contexts[activeContextIndex]?.date instanceof Date
            && !Number.isNaN(contexts[activeContextIndex].date.getTime())
          )
            ? contexts[activeContextIndex].date.getFullYear()
            : parsedFallbackDate.getFullYear(),
          fallbackDate: (
            activeContextIndex >= 0
            && contexts[activeContextIndex]?.date instanceof Date
            && !Number.isNaN(contexts[activeContextIndex].date.getTime())
          )
            ? contexts[activeContextIndex].date
            : parsedFallbackDate,
          sourceText: rawText || rowText,
        })
      : null;

    if (inlineHeading?.date) {
      const context = activeContextIndex >= 0 ? contexts[activeContextIndex] : null;
      const embeddedMoreSpecificThanContext = Boolean(
        inlineHeading.datePrecision === 'day'
        && context?.datePrecision === 'month'
      );
      const shouldPreferInlineHeading = !rowHasSpecificDate
        || rowDateKey === fallbackDateKey
        || embeddedMoreSpecificThanContext;

      if (shouldPreferInlineHeading) {
        const effectiveYearResolutionStrategy = (
          inlineHeading.yearResolutionStrategy === 'fallback_year'
          && context?.yearAdjustedFromFuture
        )
          ? context.yearResolutionStrategy
          : inlineHeading.yearResolutionStrategy;
        const effectiveYearAdjustedFromFuture = (
          inlineHeading.yearResolutionStrategy === 'fallback_year'
          && context?.yearAdjustedFromFuture
        )
          ? true
          : Boolean(inlineHeading.yearAdjustedFromFuture);
        const cleanedRowText = stripLeadingPromotedImportDateText(rowText, inlineHeading);
        const timeInspectionText = cleanedRowText || rowText;
        const extractedRowTime = inlineHeading.timeValue || extractTimeFromText(timeInspectionText).time || null;
        const inlineResolvedDate = extractedRowTime
          ? buildDateWithOptionalTime(inlineHeading.date, extractedRowTime, timeInspectionText)
          : new Date(inlineHeading.date);
        const displayRow = cleanedRowText
          ? replaceImportRowDisplayText(row, cleanedRowText, { originalRowText: rowText })
          : row;

        return withImportDateWarningMetadata({
          ...displayRow,
          date: inlineResolvedDate,
          localTime: displayRow?.localTime || extractedRowTime || null,
          timeSource: row?.timeSource && row.timeSource !== 'missing'
            ? row.timeSource
            : (extractedRowTime ? 'explicit' : row?.timeSource),
          metadata: {
            ...rowMetadata,
            datePrecision: inlineHeading.datePrecision,
            dateLabel: inlineHeading.dateLabel,
            dateHeading: inlineHeading.headingText || inlineHeading.dateLabel,
            dateContext: {
              label: inlineHeading.dateLabel,
              precision: inlineHeading.datePrecision,
              headingText: inlineHeading.headingText || inlineHeading.dateLabel,
              source: 'embedded_date_time',
              yearResolutionStrategy: effectiveYearResolutionStrategy || '',
            },
            dateSource: 'embedded_date_time',
            yearResolutionStrategy: effectiveYearResolutionStrategy || '',
            yearAdjustedFromFuture: effectiveYearAdjustedFromFuture,
          },
        }, fallbackDate);
      }
    }

    const context = activeContextIndex >= 0 ? contexts[activeContextIndex] : null;
    if (!context?.date) {
      return withImportDateWarningMetadata(row, fallbackDate);
    }

    const shouldApplyContext = !rowHasSpecificDate && (
      !rowDateKey
      || rowDateKey === fallbackDateKey
      || !hasMeaningfulTime(rowDate)
      || row?.timeSource === 'missing'
      || row?.timeSource === 'approximate'
    );

    if (!shouldApplyContext) {
      return withImportDateWarningMetadata(row, fallbackDate);
    }

    const rowTimeValue = context.timeValue
      || row?.localTime
      || (hasMeaningfulTime(rowDate) ? formatLocalTimeForStorage(rowDate) : null);
    const resolvedDate = rowTimeValue
      ? buildDateWithOptionalTime(context.date, rowTimeValue, rowText)
      : new Date(context.date);
    const contextBodyText = String(context.bodyText || '').trim();
    const cleanedText = contextBodyText || stripLeadingImportDateHeading(rowText, context);

    return withImportDateWarningMetadata({
      ...row,
      note: cleanedText || row?.note || '',
      body: cleanedText || row?.body || '',
      text: cleanedText || row?.text || '',
      content: cleanedText || row?.content || '',
      details: cleanedText || row?.details || '',
      description: cleanedText || row?.description || '',
      summary: cleanedText || row?.summary || '',
      value: cleanedText || row?.value || '',
      sourceText: cleanedText || row?.sourceText || row?.originalText || '',
      originalText: cleanedText || row?.originalText || row?.sourceText || '',
      date: resolvedDate,
      localTime: row?.localTime || context.timeValue || null,
      timeSource: row?.timeSource && row.timeSource !== 'missing'
        ? row.timeSource
        : (context.timeValue ? 'explicit' : row?.timeSource),
      metadata: {
        ...rowMetadata,
        datePrecision: context.datePrecision,
        dateLabel: context.dateLabel,
        dateHeading: context.headingText || context.dateLabel,
        dateContext: {
          label: context.dateLabel,
          precision: context.datePrecision,
          headingText: context.headingText || context.dateLabel,
          yearResolutionStrategy: context.yearResolutionStrategy || '',
        },
        yearResolutionStrategy: context.yearResolutionStrategy || '',
        yearAdjustedFromFuture: Boolean(context.yearAdjustedFromFuture),
      },
    }, fallbackDate);
  });
};
